import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import "./JoinMeeting.css";

export default function JoinMeeting({ onLeave = () => window.history.back() }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const meeting = location.state?.meeting;
  const { t } = useTranslation();

  // If accessed directly without a meeting context, return to dashboard
  useEffect(() => {
    if (!meeting) navigate("/meetingdashboard");
  }, [meeting, navigate]);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [shareOn, setShareOn] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const openFicheInNewTab = () => {
    // store meeting for the next tab
    sessionStorage.setItem("lastMeeting", JSON.stringify(meeting));
    // open new tab
    window.open("/fiche", "_blank", "noopener,noreferrer");
  };
  useEffect(() => {
    let timer;
    setElapsed(0);
    timer = setInterval(() => setElapsed((s) => s + 1), 1000);

    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      } catch (e) {
        console.warn("getUserMedia failed", e);
      }
    })();

    return () => {
      clearInterval(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [meeting?.id]);

  const toggleMic = () => {
    const stream = localVideoRef.current?.srcObject;
    stream?.getAudioTracks()?.forEach((t) => (t.enabled = !t.enabled));
    setMicOn((v) => !v);
  };

  const toggleCam = () => {
    const stream = localVideoRef.current?.srcObject;
    stream?.getVideoTracks()?.forEach((t) => (t.enabled = !t.enabled));
    setCamOn((v) => !v);
  };

  const toggleShare = async () => {
    if (!shareOn) {
      try {
        const sstream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = sstream;
        setShareOn(true);
        sstream.getVideoTracks()[0].addEventListener("ended", () => {
          setShareOn(false);
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
        });
      } catch (e) {
        console.warn("share cancelled", e);
      }
    } else {
      const ro = remoteVideoRef.current?.srcObject;
      ro?.getTracks()?.forEach((t) => t.stop());
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      setShareOn(false);
    }
  };

  const fmtTime = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };


  if (!meeting) return <div style={{ padding: 40, textAlign: 'center' }}>Loading meeting...</div>;

  const doctorName = `Dr. ${meeting.doctor_first_name || ''} ${meeting.doctor_last_name || ''}`;
  const patientName = `${meeting.patient_first_name || ''} ${meeting.patient_last_name || ''}`;
  const peerName = user?.role === 'doctor' ? patientName : doctorName;


  const handleSendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    setChatMessages([...chatMessages, { sender: 'me', text: chatInput }]);
    setChatInput("");
  };

  return (
    <div className="jm-shell">
      <header className="jm-header">
        <div className="jm-title-wrap">
          <h1 className="jm-title">{meeting?.reason || t("joinMeeting.title_default")}</h1>
          <div className="jm-sub">
            {doctorName} <span className="jm-dot" /> {patientName}
          </div>
        </div>
        <div className="jm-meta">
          <span className="jm-pill">{fmtTime(elapsed)}</span>
        </div>
      </header>

      <div className="jm-content">
        <div className="jm-stage">
          <div className="jm-remote">
            <video ref={remoteVideoRef} autoPlay playsInline className="jm-video" />
            {!remoteVideoRef.current?.srcObject && (
              <div className="jm-video-placeholder">
                <div className="jm-avatar">
                  {(peerName.split(' ')[0]?.[0] || '') + (peerName.split(' ')[1]?.[0] || '')}
                </div>
                <p className="jm-muted">{t("joinMeeting.waiting_video")}</p>
              </div>
            )}
          </div>

          <div className="jm-local">
            <video ref={localVideoRef} autoPlay playsInline muted className="jm-video" />
            {!camOn && <div className="jm-local-off">{t("joinMeeting.camera_off")}</div>}
          </div>

          <div className="jm-controls">
            <button className={`jm-ctrl ${micOn ? "" : "is-off"}`} onClick={toggleMic} title={t("joinMeeting.toggle_mic") || "Toggle microphone"}>
              {micOn ? "🎤" : "🔇"}
            </button>
            <button className={`jm-ctrl ${camOn ? "" : "is-off"}`} onClick={toggleCam} title={t("joinMeeting.toggle_cam") || "Toggle camera"}>
              {camOn ? "📷" : "📷✖"}
            </button>
            <button className={`jm-ctrl ${shareOn ? "is-on" : ""}`} onClick={toggleShare} title={t("joinMeeting.share_screen") || "Share screen"}>
              🖥️
            </button>
            <button className="jm-ctrl" onClick={() => setShowChat((v) => !v)} title={t("joinMeeting.toggle_chat") || "Toggle chat"}>
              💬
            </button>
            <button className="jm-ctrl jm-leave" onClick={onLeave} title={t("joinMeeting.leave") || "Leave meeting"}>
              ⏹ {t("joinMeeting.leave")}
            </button>
          </div>
        </div>

        <aside className="jm-side">
          <div className="jm-card">
            <div className="jm-card-title">{t("joinMeeting.meeting_info")}</div>
            <div className="jm-row">
              <span className="jm-label">{t("createMeeting.doctor")}</span>
              <span className="jm-value">{doctorName}</span>
            </div>
            <div className="jm-row">
              <span className="jm-label">{t("createMeeting.patient")}</span>
              <span className="jm-value">{patientName}</span>
            </div>
            <div className="jm-row">
              <span className="jm-label">{t("joinMeeting.start") || "Start"}</span>
              <span className="jm-value">{new Date(meeting?.start_time).toLocaleString()}</span>
            </div>
          </div>

          <div className="jm-card">
            <div className="jm-card-title">{t("joinMeeting.actions")}</div>
            <button className="jm-btn jm-btn-primary" onClick={openFicheInNewTab}>
              {t("joinMeeting.open_fiche")}
            </button>
          </div>

          <div className="jm-card">
            <div className="jm-card-title">{t("joinMeeting.participants")}</div>
            <ul className="jm-list">
              <li className="jm-li">
                <span className="jm-avatar sm">{(doctorName.split(' ')[1]?.[0] || 'D') + (doctorName.split(' ')[2]?.[0] || '')}</span>
                <span>{doctorName} {user?.role === 'doctor' && "(You)"}</span>
              </li>
              <li className="jm-li">
                <span className="jm-avatar sm">{(patientName.split(' ')[0]?.[0] || 'P') + (patientName.split(' ')[1]?.[0] || '')}</span>
                <span>{patientName} {user?.role === 'patient' && "(You)"}</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      <div className={`jm-chat ${showChat ? "open" : ""}`}>
        <div className="jm-chat-head">
          <div className="jm-card-title">{t("joinMeeting.chat")}</div>
          <button className="jm-chat-close" onClick={() => setShowChat(false)}>✖</button>
        </div>
        <div className="jm-chat-body">
          {chatMessages.length === 0 && (
            <div className="jm-msg other" style={{ opacity: 0.5, fontStyle: 'italic', textAlign: 'center', width: '100%', padding: '20px 0' }}>
              No messages yet.
            </div>
          )}
          {chatMessages.map((msg, idx) => (
            <div key={idx} className={`jm-msg ${msg.sender}`}>
              <div className="jm-bubble">{msg.text}</div>
            </div>
          ))}
        </div>
        <form className="jm-chat-input" onSubmit={handleSendChat}>
          <input
            placeholder={t("joinMeeting.type_message")}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
          />
          <button type="submit" className="jm-btn">{t("joinMeeting.send")}</button>
        </form>
      </div>
    </div>
  );
}
